import { NewChanges, NewMyWebSocketType } from './types'

export interface DataPathsType {

  delta: string
  full: string
}

export interface PartType {
  type: string
  id: string[]
  data: DataPathsType[]
  last_modified: number
}

const ROOT_URL = 'https://backend-files-public.liveresultater.no/';
const urlParams = new URLSearchParams(window.location.search)
const FORCED_INTERVAL_STR: string = urlParams.get('opdt');
let forcedInterval = Number.parseInt(FORCED_INTERVAL_STR);
if (!Number.isNaN(forcedInterval)) {
  // Ensure that we do not go below one second.
  forcedInterval = Math.max(1, forcedInterval);
}

export class NewWebsocket implements NewMyWebSocketType {
  private collectionId: string;
  private collectionsToTrigger: string[];
  private sseEventsEstablished = false;
  private lastPartHandled: Map<string, DataPathsType> = new Map();
  private killed = false;
  private hashOfLastManifest = '';

  public Close(): void {
    this.killed = true;
  }

  constructor(collectionId: string, collectionsToTrigger: string[], listenSseEvents: boolean) {
    this.collectionId = collectionId;
    this.collectionsToTrigger = collectionsToTrigger;

    this.GetData()
    if (listenSseEvents) {
      this.ListenSseEvents();
    }
  }

  // Listen for SSE (server-sent events) to get notified of changes. This is a fast-path to get updates
  // without polling. When SSE is established, we reduce our polling frequency, but keep it as a fallback.
  private ListenSseEvents(): void {
    const eventSource = new EventSource("https://sse.liveresultater.no/events/" + this.collectionId);

    eventSource.onmessage = async (event: MessageEvent) => {
      const data = JSON.parse(event.data);
      await this.HandleManifest(data);
    };

    eventSource.onopen = () => {
      this.sseEventsEstablished = true;
    };

    eventSource.onerror = () => {
      this.sseEventsEstablished = false;
    };
  }

  private async ReadFullPart(collectionName: string, part: DataPathsType, idFieldsFromManifest: string[]): Promise<void> {
    const response = await fetch(ROOT_URL + this.collectionId + '/data/' + part.full);
    const json = await response.json();

    const change: NewChanges<any> = {
      upsert: json,
      delete: []
    }

    this.DataReceived(collectionName, change, true, idFieldsFromManifest);
  }

  private async AnalyzePart(collectionName: string, part: PartType): Promise<void> {
    if (part.data.length == 0) {
      return;
    }

    let lastPartHandled: DataPathsType = null;
    if (this.lastPartHandled.has(collectionName)) {
      lastPartHandled = this.lastPartHandled.get(collectionName);
    }

    const lastPartFromBackend = part.data[part.data.length - 1];
    if (lastPartHandled == null || (lastPartFromBackend.delta == '' && lastPartFromBackend.full != lastPartHandled.full)) {
      // Read the full data
      await this.ReadFullPart(collectionName, lastPartFromBackend, part.id);

    } else {
      // Find changes between last seen and the last from backend
      let needsFullReload = false;
      let add = false;
      let needToHandle: DataPathsType[] = [];
      for (let i = 0; i < part.data.length; ++i) {
        if (add) {
          needToHandle.push(part.data[i]);

          if (part.data[i].delta == '') {
            needsFullReload = true;
            break;
          }
        }

        if (part.data[i].full == lastPartHandled.full) {
          add = true;
        }
      }

      if (!add) {
        // We did not see our last handled file, so we need a full reload.
        needsFullReload = true;
      }

      // Are any of these full reload?
      if (needsFullReload) {
        await this.ReadFullPart(collectionName, lastPartFromBackend, part.id);
      } else {
        let promises: { index: number, promise: Promise<Response> }[] = [];
        for (let i = 0; i < needToHandle.length; ++i) {
          promises.push({
            index: i,
            promise: fetch(ROOT_URL + this.collectionId + '/data/' + needToHandle[i].delta)
          });
        }

        for (const row of promises) {
          const response = await row.promise;
          const json = await response.json();

          this.DataReceived(collectionName, json, false, part.id);
        }
      }
    }


    this.lastPartHandled.set(collectionName, lastPartFromBackend);
  }

  private fnv1a32(input: string): number {
    let hash = 0x811c9dc5; // 32-bit offset basis
    for (let i = 0; i < input.length; i++) {
      hash ^= input.charCodeAt(i);
      hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
    }
    return hash >>> 0;
  }

  private async HandleManifest(data: any): Promise<number> {
    this.hashOfLastManifest = this.fnv1a32(JSON.stringify(data)).toString();
    let secondsSinceLastChange = Number.MAX_VALUE;
    let promises: Promise<any>[] = [];

    for (const val of this.collectionsToTrigger) {
      if (!(val in data)) {
        continue;
      }

      const part = data[val] as PartType;
      const nowUnixSec = Date.now() / 1000;
      secondsSinceLastChange = Math.min(secondsSinceLastChange, (nowUnixSec - part.last_modified));
      promises.push(this.AnalyzePart(val, part));
    }

    await Promise.all(promises);

    return secondsSinceLastChange
  }

  private async GetData() {
    let interval = 10000;
    try {
      const manifestFileName = Number.isNaN(forcedInterval) ? 'manifest' : 'manifest-nocache';
      const response = await fetch(ROOT_URL + this.collectionId + '/' + manifestFileName + '?hash=' + this.hashOfLastManifest);
      const data = await response.json();
      const secondsSinceLastChange = await this.HandleManifest(data);

      if (secondsSinceLastChange < 60 && !this.sseEventsEstablished) {
        interval = 2000;
      } else if (secondsSinceLastChange < (10 * 60)) {
        interval = 5000;
      }
    } catch (ex) {
      // Silently accept errors so that setTimeout still gets called below.
    }

    if (!Number.isNaN(forcedInterval)) {
      interval = forcedInterval * 1000;
    }

    if (!this.killed) {
      setTimeout(this.GetData.bind(this), interval);
    }
  }


  public DataReceived?: (collection: string, data: any, fullReload: boolean, idFieldsFromManifest: string[]) => void
}
